import {
  BufferAttribute,
  BufferGeometry,
  Color,
  Euler,
  Float32BufferAttribute,
  Group,
  Line,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Points,
  PointsMaterial,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import { Text } from 'troika-three-text';
import _ from 'underscore';

import { bufferAttributeUtils, getPositionArray } from '../../../../utils/three/buffer-attribute';
import { disposeGeometriesAndMaterials } from '../../../../utils/three/dispose';
import { concatMeshes } from '../../../../utils/three/mesh';
import { assertNever, copyMap, isDefined } from '../../../../utils/variables';
import { renderOrders } from '../../../render-orders';
import { combinePoints } from '../../../tools/utils/context-menu/design-tool/utils/point';
import { calculateArea } from '../../../utilities/math-helpers';
import { TextBatch } from '../../components/text-batch';
import { StyledLines } from '../../styled-lines/styled-lines';
import { emptyStyledLinesData, StyledLinesBuilder } from '../../styled-lines/styled-lines-data';
import { Stripe, StripePattern } from '../../styled-lines/styled-segment';
import { designColors } from '../colors';
import {
  AttributeData,
  AttributeGroupEntry,
  AttributeGroups,
  AttributeId,
  ColorCoding,
  extractSkyMapLineStyles,
  extractSkyMapPrimitiveExtras,
  SkyMapAttribute,
  SkyMapTextAttribute,
  ValueId as ValueId,
} from '../skymap-extras';
import { createIterator, IdIterator } from './create-iterator';
import { DesignNodeKDTree } from './design-node-kdtree';
import { adoptSeamLoops, getHiddenSeams } from './utils/circle';
import {
  Conic,
  getConicLength,
  getCurveConics,
  getSegmentConic,
  getSegmentCurve,
  SegmentCurve,
} from './utils/conic';
import { FaceId, FaceNode, recalculateFaceMetadata } from './utils/face-node';
import {
  getLineSegmentLengths,
  isLineSegmentStyled,
  LineSegmentId,
  LineSegmentNode,
  recalculateLineSegmentMetaData,
} from './utils/line-segment-node';
import { PointId, PointNode } from './utils/point-node';

export type Revert<T extends RevertChange['type']> = Extract<RevertChange, { type: T }>;

export type RevertChange =
  | {
      type: 'upsert-point';
      node: Pick<PointNode, 'id'> & Partial<PointNode>;
    }
  | {
      type: 'remove-point';
      id: PointId;
    }
  | {
      type: 'upsert-linesegment';
      node: Pick<LineSegmentNode, 'id'> & Partial<LineSegmentNode>;
    }
  | {
      type: 'remove-linesegment';
      id: LineSegmentId;
    }
  | {
      type: 'upsert-face';
      node: Pick<FaceNode, 'id'> & Partial<FaceNode>;
    }
  | {
      type: 'remove-face';
      id: FaceId;
    }
  | {
      type: 'custom';
      callback: () => void;
    };

export type { IdIterator } from './create-iterator';
export { createIterator } from './create-iterator';

export type DesignNodeMaterials = {
  points: PointsMaterial;
  lineSegments: LineBasicMaterial;
  faces: MeshStandardMaterial;
  texts: MeshBasicMaterial;
};

function isFaceDegenerate(a: number, b: number, c: number) {
  return a === b || a === c || b === c;
}

function isLineSegmentDegenerate(a: number, b: number) {
  return a === b;
}

function createBufferGeometry(): BufferGeometry {
  return new BufferGeometry().setAttribute('position', new Float32BufferAttribute([], 3));
}

/**
 * Contains all data for a design asset node using a graph-like strucure
 *
 * Each point of data is represented as a node. Either a point, line segment of a face.
 * The nodes keeps track internally of its relationship with other nodes and any
 * meta data associated with it.
 *
 * When the data is updated the node needs to be refreshed, which will rebuilt the THREE-data
 * based on the current state.
 */
export class DesignNode {
  private iterator = {
    point: createIterator<PointId>(),
    segment: createIterator<LineSegmentId>(),
    face: createIterator<FaceId>(),
  };

  /**
   * Contains all the data of the node.
   */
  get group() {
    return this._group;
  }

  private _group = new Group();

  readonly data = {
    points: new Points(createBufferGeometry(), new PointsMaterial()),
    lineSegments: new LineSegments(createBufferGeometry(), new LineBasicMaterial()),
    /** The line segments that are curved, have a width or stripes. */
    styledLines: new StyledLines(),
    faces: new Mesh(createBufferGeometry(), new MeshStandardMaterial()),
  } as const;

  textMaterial = new MeshBasicMaterial();

  /** The labels of the points, drawn together, since there can be thousands. */
  private readonly labels = new TextBatch();

  private readonly nodes = {
    points: new Map<PointId, PointNode>(),
    lineSegments: new Map<LineSegmentId, LineSegmentNode>(),
    faces: new Map<FaceId, FaceNode>(),
    texts: new Map<PointId, Map<AttributeId, Text>>(),
  } as const;

  /** The seams of each center point, see PointNode.seamOf. */
  private readonly seamsByCenter = new Map<PointId, Set<PointId>>();

  private readonly indexMap = {
    points: new Map<number, PointId>(),
    lineSegments: new Map<number, LineSegmentId>(),
    faces: new Map<number, FaceId>(),
  } as const;

  private _metadata = {
    pointCount: 0,
    triangleCount: 0,
    lineSegmentCount: 0,
    area: 0,
    planarArea: 0,
    length: 0,
    planarLength: 0,
  };

  get metadata() {
    return this._metadata;
  }
  get pointNodeCount() {
    return this.nodes.points.size;
  }
  get points() {
    return [...this.nodes.points.keys()];
  }
  get pointNodes() {
    return [...this.nodes.points.values()];
  }
  get lineSegmentNodeCount() {
    return this.nodes.lineSegments.size;
  }
  get lineSegments() {
    return [...this.nodes.lineSegments.keys()];
  }
  get faceNodeCount() {
    return this.nodes.faces.size;
  }
  get faces() {
    return [...this.nodes.faces.keys()];
  }
  get texts() {
    return this.nodes.texts;
  }

  rotation: Euler = new Euler();

  /**
   * @param children The primitives to create the node from. Styled lines are created from their
   * segments, with points where they meet.
   */
  constructor(
    children: (Mesh | LineSegments | Points | Line | StyledLines)[],
    options?: {
      materials?: DesignNodeMaterials;
      mergeDuplicatePoints?: boolean;
    },
  ) {
    let pointAttributes: Map<PointId, Map<AttributeId, AttributeData>> | undefined;
    let faceAttributes: Map<FaceId, Map<AttributeId, AttributeData>> | undefined;
    let lineSegmentAttributes: Map<LineSegmentId, Map<AttributeId, AttributeData>> | undefined;

    if (options?.materials) {
      this.data.faces.material = options.materials.faces;
      this.data.points.material = options.materials.points;
      this.data.lineSegments.material = options.materials.lineSegments;
      this.textMaterial = options.materials.texts;
    }

    this.data.styledLines.baseMaterial = this.data.lineSegments.material;
    this.labels.material = this.textMaterial;

    const points: {
      object: Points;
      position: Float32BufferAttribute;
    }[] = [];

    const lineSegments: {
      object: LineSegments | Line;
      styles: ReturnType<typeof extractSkyMapLineStyles>;
      position: Vector3[];
      indices: Uint32BufferAttribute;
    }[] = [];

    const faces: {
      object: Mesh;
      position: Float32BufferAttribute;
      indices: Uint32BufferAttribute;
    }[] = [];

    const styledLines: StyledLines[] = [];

    /** Seams without a center point, of circles and ellipses of drawings, see adoptSeamLoops. */
    const seams = new Set<PointId>();

    for (const child of children) {
      if (child instanceof StyledLines) {
        styledLines.push(child);
      } else if (child.geometry instanceof BufferGeometry) {
        if (child instanceof Mesh) {
          faceAttributes = extractSkyMapPrimitiveExtras<FaceId>(child.geometry);
          const result = concatMeshes([child]);
          faces.push({
            object: child,
            position: new Float32BufferAttribute(result.vertices, 3),
            indices: new Uint32BufferAttribute(result.indices, 1),
          });
        } else if (child instanceof LineSegments || child instanceof Line) {
          lineSegmentAttributes = extractSkyMapPrimitiveExtras<LineSegmentId>(child.geometry);

          const position = getPositionArray(child);
          const indices =
            child.geometry.getIndex() ??
            new Uint32BufferAttribute(
              _.times(position.length, (i) => i),
              1,
            );

          lineSegments.push({
            object: child,
            styles: extractSkyMapLineStyles(child.geometry),
            position,
            indices,
          });
        } else if (child instanceof Points) {
          pointAttributes = extractSkyMapPrimitiveExtras<PointId>(child.geometry);
          points.push({
            object: child,
            position: child.geometry.getAttribute('position') as BufferAttribute,
          });
        }
      }
    }

    for (const point of points) {
      for (let i = 0; i < point.position.count; i++) {
        const localPosition = point.object.localToWorld(
          bufferAttributeUtils.getVector3(i, point.position),
        );
        this.addPoint(
          localPosition,
          points.length === 1 ? pointAttributes?.get(i as PointId) : undefined,
        );
      }
    }

    for (const lineSegment of lineSegments) {
      const segmentPointIds = new Map<number, PointId>();

      const lineSegementsMatchesPoints = () =>
        lineSegment.position.every((vec, idx) => {
          return (
            vec.x === points[0].position.getX(idx) &&
            vec.y === points[0].position.getY(idx) &&
            vec.z === points[0].position.getZ(idx)
          );
        });

      const sharesPoints = points.length === 1 && lineSegementsMatchesPoints();

      if (sharesPoints) {
        for (const index of lineSegment.indices.array as Uint32Array) {
          if (!segmentPointIds.has(index)) {
            segmentPointIds.set(index, index as PointId);
          }
        }
      } else {
        for (const index of lineSegment.indices.array as Uint32Array) {
          if (!segmentPointIds.has(index)) {
            const localPosition = lineSegment.object.localToWorld(lineSegment.position[index]);
            const pointId = this.addPoint(localPosition).id;
            segmentPointIds.set(index, pointId);
          }
        }
      }

      const { styles } = lineSegment;

      // Seams of circles and ellipses. The center point is a vertex of the same positions, but
      // drawings have none.
      for (const [vertex, center] of styles.seams) {
        const id = segmentPointIds.get(vertex);

        if (!isDefined(id)) {
          continue;
        }

        if (isDefined(center)) {
          const centerId = sharesPoints
            ? (center as PointId)
            : segmentPointIds.get(center) ??
              this.addPoint(lineSegment.object.localToWorld(lineSegment.position[center].clone()))
                .id;
          segmentPointIds.set(center, centerId);
          this.upsertPoint({ id, seamOf: centerId });
        } else {
          seams.add(id);
        }
      }

      // The stripe patterns of each primitive are added to the patterns of the node.
      const patternOffset = this.stripePatterns.length;
      this.stripePatterns.push(...styles.patterns);

      for (let i = 0; i < lineSegment.indices.array.length; i += 2) {
        const a = segmentPointIds.get(lineSegment.indices.array[i])!;
        const b = segmentPointIds.get(lineSegment.indices.array[i + 1])!;
        const segment = i / 2;
        const stripe = styles.stripes.get(segment);
        const curve = styles.curves.get(segment);

        if (isLineSegmentDegenerate(a, b)) {
          continue;
        }

        const attributes = lineSegmentAttributes?.get(segment as LineSegmentId);
        const style = {
          width: styles.widths.get(segment),
          stripe: stripe && { ...stripe, pattern: stripe.pattern + patternOffset },
        };

        if (curve) {
          const conics = getCurveConics(
            this.getPointById(a).vertex,
            this.getPointById(b).vertex,
            'control' in curve
              ? {
                  control: lineSegment.object.localToWorld(curve.control.clone()),
                  weight: curve.weight,
                }
              : curve,
          );
          this.addCurvedLineSegments(a, b, conics, attributes, style);
        } else {
          this.addLineSegment(a, b, attributes, undefined, style);
        }
      }
    }

    for (const lines of styledLines) {
      for (const id of this.addStyledLines(lines)) {
        seams.add(id);
      }
    }

    adoptSeamLoops(this, seams);

    for (const face of faces) {
      const facePointIds = new Map<number, PointId>();
      if (points.length === 1 && bufferAttributeUtils.compare(points[0].position, face.position)) {
        for (const index of face.indices.array as Uint32Array) {
          if (!facePointIds.has(index)) {
            facePointIds.set(index, index as PointId);
          }
        }
      } else {
        for (const index of face.indices.array as Uint32Array) {
          if (!facePointIds.has(index)) {
            const localPosition = face.object.localToWorld(
              bufferAttributeUtils.getVector3(index, face.position),
            );
            const pointId = this.addPoint(localPosition).id;
            facePointIds.set(index, pointId);
          }
        }
      }

      for (let i = 0; i < face.indices.array.length; i += 3) {
        const a = facePointIds.get(face.indices.array[i])!;
        const b = facePointIds.get(face.indices.array[i + 1])!;
        const c = facePointIds.get(face.indices.array[i + 2])!;

        if (!isFaceDegenerate(a, b, c)) {
          this.addFace(
            a,
            b,
            c,
            points.length === 1 ? faceAttributes?.get((i / 3) as FaceId) : undefined,
          );
        }
      }
    }

    this.group.add(
      this.data.points,
      this.data.lineSegments,
      this.data.styledLines,
      this.data.faces,
      this.labels,
    );

    this.data.faces.renderOrder = renderOrders.designAsset.face;
    this.data.lineSegments.renderOrder = renderOrders.designAsset.lineSegment;
    this.data.styledLines.renderOrder = renderOrders.designAsset.lineSegment;
    this.data.points.renderOrder = renderOrders.designAsset.point;

    if (options?.mergeDuplicatePoints) {
      this.mergeDuplicatePoints();
    }
  }

  /**
   * Adds the items of styled lines, e.g. a drawing of a glb: line segments and curves, and
   * triangles as faces. Items that meet share a point. The widths and stripes are scaled like the
   * positions. Returns the points that are seams of the curves, see StyledLinesData.
   */
  private addStyledLines(lines: StyledLines): PointId[] {
    const scale = lines.getWorldScale(new Vector3()).x;
    const patternOffset = this.stripePatterns.length;
    this.stripePatterns.push(...lines.patterns.map((x) => x.map((length) => length * scale)));

    const getKey = (position: Vector3) => `${position.x},${position.y},${position.z}`;
    const { seams } = lines.data;
    const seamKeys = new Set(
      Array.from({ length: seams.length / 3 }, (_, i) =>
        getKey(new Vector3().fromArray(seams, i * 3)),
      ),
    );

    const pointIds = new Map<string, PointId>();
    // The corners of fills where curves have seams, e.g. a filled circle, are points of their own,
    // since a seam only joins the pieces of a curve.
    const cornerIds = new Map<string, PointId>();
    const getPointId = (position: Vector3, isCorner = false) => {
      const key = getKey(position);
      const ids = isCorner && seamKeys.has(key) ? cornerIds : pointIds;
      let id = ids.get(key);

      if (!isDefined(id)) {
        id = this.addPoint(lines.localToWorld(position.clone())).id;
        ids.set(key, id);
      }

      return id;
    };

    for (const item of lines.getItems()) {
      if (item.kind === 'triangle') {
        const a = getPointId(item.start, true);
        const b = getPointId(item.end, true);
        const c = getPointId(item.control, true);

        if (!isFaceDegenerate(a, b, c)) {
          this.addFace(a, b, c);
        }

        continue;
      }

      const a = getPointId(item.start);
      const b = getPointId(item.end);

      // Dots have no length.
      if (isLineSegmentDegenerate(a, b)) {
        continue;
      }

      const curve =
        item.kind === 'curve'
          ? getSegmentCurve({
              start: this.getPointById(a).vertex,
              control: lines.localToWorld(item.control.clone()),
              end: this.getPointById(b).vertex,
              weight: item.weight,
            })
          : undefined;

      this.addLineSegment(a, b, undefined, curve, {
        width: item.width > 0 ? item.width * scale : undefined,
        stripe: item.stripe && {
          ...item.stripe,
          pattern: item.stripe.pattern + patternOffset,
          offset: item.stripe.offset * scale,
        },
      });
    }

    return [...seamKeys].map((key) => pointIds.get(key)).filter(isDefined);
  }

  /**
   * Adds line segments along the curves from start to end, with points where they meet, e.g. an arc
   * that is too large for one curve. The stripes continue along the line segments.
   * Without curves, it is one straight line segment.
   */
  addCurvedLineSegments(
    start: PointId,
    end: PointId,
    conics: Conic[],
    attributeData?: Map<AttributeId, AttributeData>,
    style?: { width?: number; stripe?: Stripe },
  ): RevertChange[] {
    if (conics.length === 0) {
      return [this.addLineSegment(start, end, attributeData, undefined, style)];
    }

    const changes: RevertChange[] = [];
    let previous = start;
    let offset = 0;

    conics.forEach((conic, i) => {
      let next = end;

      if (i < conics.length - 1) {
        const change = this.addPoint(conic.end.clone());
        changes.unshift(change);
        next = change.id;
      }

      const stripe = style?.stripe && { ...style.stripe, offset: style.stripe.offset + offset };
      changes.unshift(
        this.addLineSegment(previous, next, attributeData, getSegmentCurve(conic), {
          width: style?.width,
          stripe,
        }),
      );

      offset += getConicLength(conic);
      previous = next;
    });

    return changes;
  }

  attributes: Map<AttributeId, SkyMapAttribute> = new Map();
  /**
   * The stripe patterns that the stripes of the line segments refer to.
   */
  stripePatterns: StripePattern[] = [];
  attributeGroups: AttributeGroups = new Map();
  colorCoding: ColorCoding = { enabled: false };
  private colorMap?: Map<ValueId, Color> = undefined;

  /**
   * Finds any point nodes with same position and merges them.
   * This will prevent issues with triangulation and segmented polylines for example.
   */
  mergeDuplicatePoints() {
    const kdTree = new DesignNodeKDTree(this);

    const points = this.pointNodes;
    const mergeThreshold = 0.001; // 1 mm.
    for (const node of points) {
      if (!this.nodes.points.has(node.id)) {
        continue;
      }

      const nearbyPoints = kdTree.rangeSearch(node, mergeThreshold);

      for (const nearbyPoint of nearbyPoints) {
        if (!nearbyPoint.node || nearbyPoint.node.id === node.id) {
          continue;
        }

        if (this.nodes.points.has(nearbyPoint.node.id)) {
          combinePoints(this, node.id, nearbyPoint.node.id);
        }
      }
    }
  }

  setColorCoding(colorCoding: ColorCoding, colorMap?: Map<ValueId, Color>) {
    this.colorCoding = colorCoding;
    this.colorMap = colorMap;
  }

  /**
   * Refreshes the points lines and faces based on the current state.
   * As well as refreshing the metadata.
   */
  refresh() {
    const iterators = {
      point: createIterator<number>(),
      lineSegments: createIterator<number>(),
      faces: createIterator<number>(),
    };

    const pointPositionArray = new Float32Array(this.pointNodeCount * 3);
    const pointColorArray = new Float32Array(this.pointNodeCount * 3);

    const styledLineSegments = [...this.nodes.lineSegments.values()].filter(isLineSegmentStyled);
    const straightLineSegmentCount = this.lineSegmentNodeCount - styledLineSegments.length;
    const lineSegmentPositionArray = new Float32Array(straightLineSegmentCount * 3 * 2);
    const lineSegmentColorArray = new Float32Array(straightLineSegmentCount * 3 * 2);

    const facePositionArray = new Float32Array(this.faceNodeCount * 3 * 3);
    const faceColorArray = new Float32Array(this.faceNodeCount * 3 * 3);

    this.indexMap.points.clear();
    this.indexMap.lineSegments.clear();
    this.indexMap.faces.clear();

    const getNodeVertex = (pointId: PointId) => {
      return this.nodes.points.get(pointId)!.vertex;
    };

    const setNextVertex = (
      position: Float32Array,
      color: Float32Array,
      iterator: IdIterator<number>,
      vertex: Vector3,
      r: number,
      g: number,
      b: number,
    ) => {
      const x = iterator.next();
      const y = iterator.next();
      const z = iterator.next();

      position[x] = vertex.x;
      position[y] = vertex.y;
      position[z] = vertex.z;

      color[x] = r;
      color[y] = g;
      color[z] = b;

      return x / 3;
    };

    const white = new Color(1, 1, 1);
    const colorsAvailable =
      this.colorCoding.enabled &&
      isDefined(this.colorCoding.attributeId) &&
      isDefined(this.colorMap);
    const getColor = (defaultColor: Color, attributes: Map<AttributeId, AttributeData>) => {
      if (this.colorCoding.enabled) {
        if (!colorsAvailable) {
          return white;
        }

        const data = attributes.get(this.colorCoding.attributeId!);
        const color = data?.type === 'enum' ? this.colorMap!.get(data.index) : undefined;
        return color ?? white;
      }

      return defaultColor;
    };

    for (const labelMap of this.nodes.texts.values()) {
      for (const label of labelMap.values()) {
        label.visible = false;
      }
    }

    const textAttributes: SkyMapTextAttribute[] = [...this.attributes.values()].filter(
      (x) => x.type === 'string' && x.display,
    ) as SkyMapTextAttribute[];

    // The seams of intact circles are after the other points, outside the draw range, so that they
    // are neither drawn nor picked, but have an index for intersections with them.
    const hiddenSeams = getHiddenSeams(this);
    const pointNodes = [...this.nodes.points.values()];
    const orderedPointNodes = [
      ...pointNodes.filter((x) => !hiddenSeams.has(x.id)),
      ...pointNodes.filter((x) => hiddenSeams.has(x.id)),
    ];

    for (const node of orderedPointNodes) {
      const color = getColor(designColors.defaultPointColor, node.attributes);
      const index = setNextVertex(
        pointPositionArray,
        pointColorArray,
        iterators.point,
        node.vertex,
        color.r,
        color.g,
        color.b,
      );
      this.indexMap.points.set(index, node.id);

      if (!this.nodes.texts.has(node.id)) {
        this.nodes.texts.set(node.id, new Map());
      }

      const labelMap = this.nodes.texts.get(node.id)!;

      for (const attribute of textAttributes) {
        const attributeData = node.attributes.get(attribute.id);
        if (attributeData?.type === 'string') {
          let label = labelMap.get(attribute.id);

          // Increase the size with a few % to match the font sizing displayed in TrueView.
          const sizeFactor = 1.33;
          const fontSize = (attributeData?.height ?? 0.5) * sizeFactor;

          if (!isDefined(label)) {
            const newLabel = new Text();
            this.labels.add(newLabel);
            newLabel.anchorY = 'bottom-baseline';
            void newLabel.sync();
            labelMap.set(attribute.id, newLabel);
            label = newLabel;
          }

          label.text = attributeData.value;
          label.fontSize = fontSize;
          label.position.copy(node.vertex);
          label.color = color.getHex();
          label.setRotationFromAxisAngle(new Vector3(0, 0, 1), attributeData.rotation ?? 0);

          labelMap.get(attribute.id)!.visible = this.group.parent?.visible ?? false;
        }
      }
    }

    for (const node of this.nodes.lineSegments.values()) {
      if (isLineSegmentStyled(node)) {
        continue;
      }

      const color = getColor(designColors.defaultLineSegmentColor, node.attributes);

      const index = setNextVertex(
        lineSegmentPositionArray,
        lineSegmentColorArray,
        iterators.lineSegments,
        getNodeVertex(node.values[0]),
        color.r,
        color.g,
        color.b,
      );

      setNextVertex(
        lineSegmentPositionArray,
        lineSegmentColorArray,
        iterators.lineSegments,
        getNodeVertex(node.values[1]),
        color.r,
        color.g,
        color.b,
      );
      this.indexMap.lineSegments.set(index, node.id);
    }

    const builder = new StyledLinesBuilder(this.stripePatterns);

    styledLineSegments.forEach((node, i) => {
      // The styled line segments are indexed after the straight ones, which have 2 vertices each.
      const index = (straightLineSegmentCount + i) * 2;
      this.indexMap.lineSegments.set(index, node.id);

      const start = getNodeVertex(node.values[0]);
      const end = getNodeVertex(node.values[1]);
      const style = {
        width: node.width,
        stripe: node.stripe,
        color: getColor(designColors.defaultLineSegmentColor, node.attributes),
        id: index,
      };

      // Line segments that share a point are joined.
      if (node.curve) {
        builder.addCurve(getSegmentConic(start, end, node.curve), style, node.values);
      } else {
        builder.addSegment(start, end, style, node.values);
      }
    });

    this.data.styledLines.setData(builder.build()[0] ?? emptyStyledLinesData(this.stripePatterns));

    for (const node of this.nodes.faces.values()) {
      const color = getColor(designColors.defaultFaceColor, node.attributes);
      const index = setNextVertex(
        facePositionArray,
        faceColorArray,
        iterators.faces,
        getNodeVertex(node.values[0]),
        color.r,
        color.g,
        color.b,
      );
      setNextVertex(
        facePositionArray,
        faceColorArray,
        iterators.faces,
        getNodeVertex(node.values[1]),
        color.r,
        color.g,
        color.b,
      );
      setNextVertex(
        facePositionArray,
        faceColorArray,
        iterators.faces,
        getNodeVertex(node.values[2]),
        color.r,
        color.g,
        color.b,
      );
      this.indexMap.faces.set(index / 3, node.id);
    }

    const updateGeometry = (
      geometry: BufferGeometry,
      position: Float32Array,
      color: Float32Array,
    ) => {
      geometry.setAttribute('position', new Float32BufferAttribute(position, 3));
      geometry.setAttribute('color', new Float32BufferAttribute(color, 3));
      geometry.computeBoundingSphere();
    };

    updateGeometry(this.data.points.geometry, pointPositionArray, pointColorArray);
    this.data.points.geometry.setDrawRange(0, this.pointNodeCount - hiddenSeams.size);
    updateGeometry(
      this.data.lineSegments.geometry,
      lineSegmentPositionArray,
      lineSegmentColorArray,
    );
    updateGeometry(this.data.faces.geometry, facePositionArray, faceColorArray);
  }

  /**
   * @param seamOf The center point of the circle or ellipse that the point is a seam of.
   */
  addPoint(
    vector: Vector3,
    attributeData?: Map<AttributeId, AttributeData>,
    seamOf?: PointId,
  ): Revert<'remove-point'> {
    const id = this.iterator.point.next();
    this.nodes.points.set(id, {
      id,
      vertex: vector,
      faces: [],
      segments: [],
      attributes: attributeData ? copyMap(attributeData, { deep: true }) : new Map(),
      seamOf,
    });
    this.indexSeam(id, undefined, seamOf);

    return {
      type: 'remove-point',
      id: id,
    };
  }

  removePoint(id: PointId): Revert<'upsert-point'> {
    const point = this.getPointById(id);

    if (point.faces.length > 0 || point.segments.length > 0) {
      throw new Error(
        'Error removing point. There are segments or faces attached. Removing the point would result in dangling data.',
      );
    }

    this.nodes.points.delete(point.id);
    this.indexSeam(point.id, point.seamOf, undefined);

    return {
      type: 'upsert-point',
      node: point,
    };
  }

  /**
   * Gets the points that are seams of the center point, see PointNode.seamOf.
   */
  getSeams(center: PointId): PointId[] {
    return [...(this.seamsByCenter.get(center) ?? [])];
  }

  hasPoint(id: PointId) {
    return this.nodes.points.has(id);
  }

  private indexSeam(id: PointId, from: PointId | undefined, to: PointId | undefined) {
    if (from === to) {
      return;
    }

    if (isDefined(from)) {
      this.seamsByCenter.get(from)?.delete(id);
    }

    if (isDefined(to)) {
      const seams = this.seamsByCenter.get(to) ?? new Set();
      seams.add(id);
      this.seamsByCenter.set(to, seams);
    }
  }

  removeFace(id: FaceId): Revert<'upsert-face'> {
    const face = this.getFaceById(id);

    const a = this.getPointById(face.values[0]);
    const b = this.getPointById(face.values[1]);
    const c = this.getPointById(face.values[2]);

    a.faces = a.faces.filter((f) => f !== face.id);
    b.faces = b.faces.filter((f) => f !== face.id);
    c.faces = c.faces.filter((f) => f !== face.id);

    this.nodes.faces.delete(face.id);

    return {
      type: 'upsert-face',
      node: face,
    };
  }

  removeLineSegment(id: LineSegmentId): Revert<'upsert-linesegment'> {
    const lineSegment = this.getLineSegmentById(id);

    const start = this.getPointById(lineSegment.values[0]);
    const end = this.getPointById(lineSegment.values[1]);

    start.segments = start.segments.filter((f) => f !== lineSegment.id);
    end.segments = end.segments.filter((f) => f !== lineSegment.id);

    this.nodes.lineSegments.delete(lineSegment.id);

    return {
      type: 'upsert-linesegment',
      node: lineSegment,
    };
  }

  dispose() {
    this.group.removeFromParent();

    // Also frees the labels.
    this.labels.dispose();

    // TODO: Dispose of geometry.
    disposeGeometriesAndMaterials(this.data.faces);
    disposeGeometriesAndMaterials(this.data.lineSegments);
    this.data.styledLines.dispose();
    disposeGeometriesAndMaterials(this.data.points);
  }

  /**
   * @param curve Curves the line segment, see conic.ts.
   * @param style The width and stripes of the line segment.
   */
  addLineSegment(
    start: PointId,
    end: PointId,
    attributeData?: Map<AttributeId, AttributeData>,
    curve?: SegmentCurve,
    style?: { width?: number; stripe?: Stripe },
  ): Revert<'remove-linesegment'> {
    if (isLineSegmentDegenerate(start, end)) {
      throw new Error('Unable to create line segment. Degenerate geometry.');
    }

    const nodeStart = this.getPointById(start);
    const nodeEnd = this.getPointById(end);

    const id = this.iterator.segment.next();

    this.nodes.lineSegments.set(id, {
      id,
      values: [start, end],
      attributes: attributeData ? copyMap(attributeData, { deep: true }) : new Map(),
      curve: curve && { ...curve },
      width: style?.width ?? 0,
      stripe: style?.stripe && { ...style.stripe },
      ...getLineSegmentLengths(nodeStart.vertex, nodeEnd.vertex, curve),
    });

    nodeStart.segments.push(id);
    nodeEnd.segments.push(id);

    return {
      type: 'remove-linesegment',
      id: id,
    };
  }

  addFace(
    a: PointId,
    b: PointId,
    c: PointId,
    attributeData?: Map<AttributeId, AttributeData>,
  ): Revert<'remove-face'> {
    if (isFaceDegenerate(a, b, c)) {
      throw new Error('Unable to create face. Degenerate geometry.');
    }

    const nodeA = this.getPointById(a);
    const nodeB = this.getPointById(b);
    const nodeC = this.getPointById(c);

    const id = this.iterator.face.next();

    const vertices = [
      nodeA.vertex.x,
      nodeA.vertex.y,
      nodeA.vertex.z,
      nodeB.vertex.x,
      nodeB.vertex.y,
      nodeB.vertex.z,
      nodeC.vertex.x,
      nodeC.vertex.y,
      nodeC.vertex.z,
    ];

    this.nodes.faces.set(id, {
      id,
      values: [a, b, c],
      attributes: attributeData ? copyMap(attributeData, { deep: true }) : new Map(),
      area: calculateArea(vertices, [0, 1, 2], false),
      planarArea: calculateArea(vertices, [0, 1, 2], true),
    });

    nodeA.faces.push(id);
    nodeB.faces.push(id);
    nodeC.faces.push(id);

    return {
      type: 'remove-face',
      id: id,
    };
  }

  upsertPoint(
    data: Pick<PointNode, 'id'> & Partial<PointNode>,
  ): Revert<'upsert-point'> | Revert<'remove-point'> {
    const oldNode = this.nodes.points.get(data.id);

    const faces = data.faces ?? oldNode?.faces ?? [];
    const segments = data.segments ?? oldNode?.segments ?? [];

    const newNode = {
      id: data.id,
      faces: faces.slice(),
      segments: segments.slice(),
      vertex: data.vertex ?? oldNode?.vertex ?? new Vector3(0, 0, 0),
      attributes: data.attributes ?? oldNode?.attributes ?? new Map(),
      seamOf: 'seamOf' in data ? data.seamOf : oldNode?.seamOf,
    };

    this.nodes.points.set(data.id, newNode);
    this.indexSeam(data.id, oldNode?.seamOf, newNode.seamOf);

    recalculateFaceMetadata(this, newNode.faces);
    recalculateLineSegmentMetaData(this, newNode.segments);

    if (isDefined(oldNode)) {
      return {
        type: 'upsert-point',
        node: oldNode,
      };
    }

    return {
      type: 'remove-point',
      id: data.id,
    };
  }

  upsertLineSegment(
    data: Pick<LineSegmentNode, 'id'> & Partial<LineSegmentNode>,
  ): Revert<'upsert-linesegment'> | Revert<'remove-linesegment'> {
    const oldNode = this.nodes.lineSegments.get(data.id);

    if (isDefined(data.values)) {
      if (_.uniq(data.values).length !== 2) {
        throw Error('Unable to upsert line segment. Degenerate geometry.');
      }

      if (isDefined(oldNode)) {
        const a = this.getPointById(oldNode.values[0]);
        const b = this.getPointById(oldNode.values[1]);
        a.segments = a.segments.filter((x) => x !== oldNode.id);
        b.segments = b.segments.filter((x) => x !== oldNode.id);
      }

      const a = this.getPointById(data.values[0]);
      const b = this.getPointById(data.values[1]);

      a?.segments.push(data.id);
      b?.segments.push(data.id);
    }

    this.nodes.lineSegments.set(data.id, {
      id: data.id,
      values: data.values ?? oldNode?.values ?? ([0, 0] as [PointId, PointId]),
      attributes: data.attributes ?? oldNode?.attributes ?? new Map(),
      curve: 'curve' in data ? data.curve : oldNode?.curve,
      width: data.width ?? oldNode?.width ?? 0,
      stripe: 'stripe' in data ? data.stripe : oldNode?.stripe,
      length: data.length ?? oldNode?.length ?? 0,
      planarLength: data.planarLength ?? oldNode?.planarLength ?? 0,
    });

    if (isDefined(oldNode)) {
      return {
        type: 'upsert-linesegment',
        node: oldNode,
      };
    }

    return {
      type: 'remove-linesegment',
      id: data.id,
    };
  }

  upsertFace(
    data: Pick<FaceNode, 'id'> & Partial<FaceNode>,
  ): Revert<'upsert-face'> | Revert<'remove-face'> {
    const oldNode = this.nodes.faces.get(data.id);

    if (isDefined(data.values)) {
      if (_.uniq(data.values).length !== 3) {
        throw Error('Unable to upsert face. Degenerate geometry.');
      }

      if (isDefined(oldNode)) {
        const a = this.getPointById(oldNode.values[0]);
        const b = this.getPointById(oldNode.values[1]);
        const c = this.getPointById(oldNode.values[2]);

        a.faces = a.faces.filter((x) => x !== oldNode.id);
        b.faces = b.faces.filter((x) => x !== oldNode.id);
        c.faces = c.faces.filter((x) => x !== oldNode.id);
      }

      const a = this.getPointById(data.values[0]);
      const b = this.getPointById(data.values[1]);
      const c = this.getPointById(data.values[2]);

      a.faces.push(data.id);
      b.faces.push(data.id);
      c.faces.push(data.id);
    }

    this.nodes.faces.set(data.id, {
      id: data.id,
      values: data.values ?? oldNode?.values ?? ([0, 0, 0] as [PointId, PointId, PointId]),
      attributes: data.attributes ?? oldNode?.attributes ?? new Map(),
      area: data.area ?? oldNode?.area ?? 0,
      planarArea: data.planarArea ?? oldNode?.planarArea ?? 0,
    });

    if (isDefined(oldNode)) {
      return {
        type: 'upsert-face',
        node: oldNode,
      };
    }

    return {
      type: 'remove-face',
      id: data.id,
    };
  }

  /**
   * @throws Not found
   */
  getLineSegmentById(id: LineSegmentId): LineSegmentNode {
    const node = this.nodes.lineSegments.get(id);
    if (!isDefined(node)) {
      throw new Error('Error getting line segment. Line segment not found.');
    }
    return node;
  }

  /**
   * @param index The "index" value returned when performing an intersection against
   * the linesegments object
   * @throws Not found
   */
  getLineSegmentByIndex(index: number): LineSegmentNode {
    const id = this.indexMap.lineSegments.get(index);
    if (!isDefined(id)) {
      throw new Error('Error getting line segment id. Index not found in index map.');
    }
    return this.getLineSegmentById(id);
  }

  findLineSegmentByPointIds(a: PointId, b: PointId): LineSegmentNode | undefined {
    const segments = _.intersection(
      this.nodes.points.get(a)?.segments ?? [],
      this.nodes.points.get(b)?.segments ?? [],
    );

    return segments.length > 0 ? this.getLineSegmentById(segments[0]) : undefined;
  }

  findFaceByPointIds(a: PointId, b: PointId, c: PointId): FaceNode | undefined {
    const faces = _.intersection(
      this.nodes.points.get(a)?.faces ?? [],
      this.nodes.points.get(b)?.faces ?? [],
      this.nodes.points.get(c)?.faces ?? [],
    );

    return faces.length > 0 ? this.getFaceById(faces[0]) : undefined;
  }

  /**
   * @throws Not found
   */
  getPointById(id: PointId): PointNode {
    const node = isDefined(id) ? this.nodes.points.get(id) : undefined;
    if (!isDefined(node)) {
      throw new Error('Error getting point by ID, point node not found.');
    }
    return node;
  }

  /**
   * @param index The "index" value returned when performing an intersection against
   * the points object
   * @throws Not found
   */
  getPointByIndex(index: number): PointNode {
    const id = this.indexMap.points.get(index);
    if (!isDefined(id)) {
      throw new Error('Error getting point by index. Index not found in index map.');
    }
    return this.getPointById(id);
  }

  /**
   * Finds the index for point with id.
   */
  findIndexByPointId(id: PointId): number | undefined {
    for (const entry of this.indexMap.points.entries()) {
      if (entry[1] === id) {
        return entry[0];
      }
    }
  }

  /**
   * Finds the index for the line segment with id, see getLineSegmentByIndex.
   */
  findIndexByLineSegmentId(id: LineSegmentId): number | undefined {
    for (const [index, lineSegmentId] of this.indexMap.lineSegments.entries()) {
      if (lineSegmentId === id) {
        return index;
      }
    }
  }

  /**
   * @param faceIndex The "faceIndex" value returned when performing an intersection against
   * the mesh object
   * @throws Not found
   */
  getFaceByIndex(faceIndex: number): FaceNode {
    const id = this.indexMap.faces.get(faceIndex);
    if (!isDefined(id)) {
      throw new Error('Error finding face. Index not found in index map.');
    }
    return this.getFaceById(id);
  }

  /**
   * @throws Not found
   */
  getFaceById(id: FaceId): FaceNode {
    const node = this.nodes.faces.get(id);
    if (!isDefined(node)) {
      throw new Error('Error finding face. Face node not found.');
    }
    return node;
  }

  getWorldPosition(localPosition: Vector3) {
    return this.group.localToWorld(localPosition.clone());
  }

  getLocalPosition(worldPosition: Vector3) {
    return this.group.worldToLocal(worldPosition.clone());
  }

  /**
   * @throws Not found
   */
  getVertexByPointId(pointId: PointId, mode: 'local' | 'world') {
    const node = this.getPointById(pointId);

    if (mode === 'world') {
      return this.group.localToWorld(node.vertex.clone());
    }

    return node.vertex.clone();
  }

  /**
   * @throws Not found
   */
  getVerticesByLineSegmentId(id: LineSegmentId, mode: 'local' | 'world'): [Vector3, Vector3] {
    const node = this.getLineSegmentById(id);

    return [
      this.getVertexByPointId(node.values[0], mode),
      this.getVertexByPointId(node.values[1], mode),
    ];
  }

  /**
   * @throws Not found
   */
  getVerticesByFaceId(id: FaceId, mode: 'local' | 'world'): [Vector3, Vector3, Vector3] {
    const node = this.getFaceById(id);

    return [
      this.getVertexByPointId(node.values[0], mode),
      this.getVertexByPointId(node.values[1], mode),
      this.getVertexByPointId(node.values[2], mode),
    ];
  }

  getVertexByIndex(index: number, mode: 'local' | 'world'): Vector3 {
    const node = this.getPointByIndex(index);

    if (mode === 'world') {
      return this.group.localToWorld(node.vertex.clone());
    }

    return node.vertex.clone();
  }

  revert(changes: RevertChange[]) {
    for (const change of changes) {
      console.log('applying revert', change);
      switch (change.type) {
        case 'remove-point':
          this.removePoint(change.id);
          this.iterator.point.decrement();
          continue;
        case 'upsert-point':
          this.upsertPoint(change.node);
          continue;
        case 'remove-linesegment':
          this.removeLineSegment(change.id);
          this.iterator.segment.decrement();
          continue;
        case 'upsert-linesegment':
          this.upsertLineSegment(change.node);
          continue;
        case 'remove-face':
          this.removeFace(change.id);
          this.iterator.face.decrement();
          continue;
        case 'upsert-face':
          this.upsertFace(change.node);
          continue;
        case 'custom':
          change.callback();
          continue;
        default:
          assertNever(change);
      }
    }
  }

  getColorAttribute() {
    const attribute = this.colorCoding.attributeId
      ? this.attributes.get(this.colorCoding.attributeId)
      : undefined;
    if (attribute?.type === 'enum') {
      return attribute;
    }
  }
  getAttribute(id: AttributeId): SkyMapAttribute {
    const attribute = this.attributes.get(id);
    if (!isDefined(attribute)) {
      throw new Error('Error getting attribute. Attribute not found.');
    }
    return attribute;
  }

  getEnumAttribute(id: AttributeId) {
    const attribute = this.getAttribute(id);
    if (attribute.type !== 'enum') {
      throw new Error('Error getting attribute. Not an enum attribute.');
    }
    return attribute;
  }

  getAttributes(): SkyMapAttribute[] {
    return [...this.attributes.values()];
  }

  getAttributeGroups(): { name: string; entries: AttributeGroupEntry[] }[] {
    return [...this.attributeGroups.entries()].map((x) => {
      return {
        name: x[0],
        entries: x[1],
      };
    });
  }

  addAttribute(attribute: SkyMapAttribute): RevertChange {
    this.attributes.set(attribute.id, attribute);

    return {
      type: 'custom',
      callback: () => {
        this.attributes.delete(attribute.id);
      },
    };
  }

  addAttributeGroup(name: string, entries: AttributeGroupEntry[]): RevertChange {
    this.attributeGroups.set(name, entries);
    return {
      type: 'custom',
      callback: () => {
        this.attributeGroups.delete(name);
      },
    };
  }

  removeAttributeGroup(name: string): RevertChange {
    const data = this.attributeGroups.get(name);
    this.attributeGroups.delete(name);
    return {
      type: 'custom',
      callback: () => {
        if (isDefined(data)) {
          this.attributeGroups.set(name, data);
        }
      },
    };
  }

  removeAttribute(id: AttributeId): RevertChange {
    const attribute = this.getAttribute(id);

    const changes: (() => void)[] = [];
    for (const group of this.attributeGroups.values()) {
      for (const entry of group) {
        const data = entry.data.get(attribute.id);
        if (isDefined(data)) {
          entry.data.delete(attribute.id);
          changes.push(() => {
            entry.data.set(attribute.id, data);
          });
        }
      }
    }

    this.attributes.delete(id);

    return {
      type: 'custom',
      callback: () => {
        changes.map((x) => x());
        this.attributes.set(id, attribute);
      },
    };
  }
}
